import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { ProjectAvatar } from "@/workshop/components/panels/project-avatar";
import type { BrowseProject } from "./browse-tree";

/* ---------- ColorSubmenuItem — hover to show swatch flyout ---------- */

export const PRESET_COLORS = [
  "#30D158",
  "#5E5CE6",
  "#FF6B6B",
  "#FF9F0A",
  "#FFD60A",
  "#64D2FF",
  "#BF5AF2",
  "#FF375F",
];

interface ColorSubmenuItemProps {
  currentColor?: string;
  onSelect: (color: string) => void;
  "data-testid"?: string;
}

export function ColorSubmenuItem({
  currentColor,
  onSelect,
  "data-testid": testId,
}: ColorSubmenuItemProps) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={testId}
    >
      <button
        type="button"
        role="menuitem"
        className={cn(
          "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11px] text-primary transition-colors",
          "hover:bg-glass-fill-heavy focus:outline-none",
        )}
      >
        <Icons.Palette className="h-3 w-3" />
        Set color
        <span className="flex-1" />
        <Icons.ChevronRight className="h-3 w-3 text-muted" />
      </button>
      {open && (
        <div className="absolute left-full top-0 z-[10000] pl-2">
          <div className="rounded-lg border border-glass-stroke bg-glass-fill-dense p-3 shadow-glass backdrop-blur-md">
            <div className="grid w-[152px] grid-cols-4 gap-3">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-lg transition-transform hover:scale-110",
                    color === currentColor &&
                      "ring-2 ring-white ring-offset-2 ring-offset-transparent",
                  )}
                  style={{ backgroundColor: color }}
                  onClick={() => onSelect(color)}
                  data-testid={`color-swatch-${color.replace("#", "")}`}
                >
                  {color === currentColor && (
                    <Icons.Check className="h-3 w-3 text-white drop-shadow-sm" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- MoveSubmenuItem — hover to show project list flyout ---------- */

interface MoveSubmenuItemProps {
  candidates: BrowseProject[];
  onSelect: (toProjectId: string) => void;
  "data-testid"?: string;
}

export function MoveSubmenuItem({
  candidates,
  onSelect,
  "data-testid": testId,
}: MoveSubmenuItemProps) {
  const [open, setOpen] = useState(false);
  const disabled = candidates.length === 0;

  return (
    <div
      className="relative"
      onMouseEnter={() => !disabled && setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={testId}
    >
      <button
        type="button"
        role="menuitem"
        disabled={disabled}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11px] transition-colors",
          "focus:outline-none",
          disabled
            ? "cursor-not-allowed text-muted opacity-60"
            : "text-primary hover:bg-glass-fill-heavy",
        )}
      >
        <Icons.ArrowRight className="h-3 w-3" />
        Move to
        <span className="flex-1" />
        {!disabled && <Icons.ChevronRight className="h-3 w-3 text-muted" />}
      </button>
      {open && !disabled && (
        <div className="absolute left-full top-0 z-[10000] pl-2">
          <div className="min-w-[160px] rounded-lg border border-glass-stroke bg-glass-fill-dense p-1 shadow-glass backdrop-blur-md">
            {candidates.map((p) => (
              <button
                key={p.id}
                type="button"
                role="menuitem"
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[11px] text-primary transition-colors",
                  "hover:bg-glass-fill-heavy focus:outline-none",
                )}
                onClick={() => onSelect(p.id)}
                data-testid={`browse-tree-move-target-${p.id}`}
              >
                <ProjectAvatar name={p.name} color={p.color} size="sm" />
                <span className="flex-1 truncate">{p.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- SpaceIcon — 2×2 grid of small squares ---------- */

export function SpaceIcon({
  className,
  color,
}: {
  className?: string;
  color?: string;
}) {
  const dotStyle = color ? { backgroundColor: color, opacity: 0.7 } : undefined;
  return (
    <span
      className={cn("inline-grid shrink-0 grid-cols-2 gap-[2px]", className)}
      aria-hidden="true"
    >
      <span
        className="h-[5px] w-[5px] rounded-[1px] bg-current opacity-70"
        style={dotStyle}
      />
      <span
        className="h-[5px] w-[5px] rounded-[1px] bg-current opacity-70"
        style={dotStyle}
      />
      <span
        className="h-[5px] w-[5px] rounded-[1px] bg-current opacity-70"
        style={dotStyle}
      />
      <span
        className="h-[5px] w-[5px] rounded-[1px] bg-current opacity-70"
        style={dotStyle}
      />
    </span>
  );
}
